import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InboxIcon } from '@/components/catalog/icons';
import { EmailThreadList } from '@/components/email-log/EmailThreadList';
import { EmailThreadModal } from '@/components/email-log/EmailThreadModal';
import { DENIED } from '@/components/email-log/email-log-format';
import {
  Button,
  EmptyState,
  Field,
  Input,
  ListPager,
  PageHeader,
  Panel,
  SegmentedControl,
  Skeleton,
  type SegmentedControlOption,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import type {
  EmailLogStatus,
  EmailThread,
  EmailThreadListParams,
  EmailThreadMatchStatus,
  Paginated,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/*
 * Email Log — the triage queue for BCC'd mail (GET /email-log/threads).
 *
 * URL keys: `status` (tab; ABSENT = Unmatched, the default — `unmatched`,
 * `ambiguous`, `matched`, `ignored` are each one tab and each one server
 * `status` value, so every tab pages correctly), `search`, `page`. A tab switch
 * clears `search` and `page`.
 *
 * Gates: the page on `email-log.read`; Assign / Ignore on `email-log.triage`.
 * The server's 403 is the real boundary.
 */

const PER_PAGE = 25;
/** The server's `perPage` ceiling; "Show all" asks for one page of this size. */
const SHOW_ALL_PER_PAGE = 100;
const SEARCH_DEBOUNCE_MS = 250;

const TAB_OPTIONS: readonly SegmentedControlOption<EmailThreadMatchStatus>[] = [
  { value: 'unmatched', label: 'Unmatched' },
  { value: 'ambiguous', label: 'Ambiguous' },
  { value: 'matched', label: 'Logged' },
  { value: 'ignored', label: 'Ignored' },
];

const STATUSES: ReadonlySet<string> = new Set(TAB_OPTIONS.map((tab) => tab.value));

const EMPTY_TITLES: Record<EmailThreadMatchStatus, string> = {
  unmatched: 'No unmatched threads',
  ambiguous: 'No ambiguous threads',
  matched: 'No logged threads yet',
  ignored: 'No ignored threads',
};

interface ParsedQuery {
  status: EmailThreadMatchStatus;
  search: string;
  page: number;
}

function parseQuery(sp: URLSearchParams): ParsedQuery {
  const status = sp.get('status') ?? '';
  const rawPage = Number(sp.get('page') ?? '');
  return {
    status: STATUSES.has(status) ? (status as EmailThreadMatchStatus) : 'unmatched',
    search: sp.get('search')?.trim() ?? '',
    page: Number.isInteger(rawPage) && rawPage > 1 ? rawPage : 1,
  };
}

function MailboxStatus({
  status,
  error,
}: {
  status: EmailLogStatus | null;
  error: string | null;
}) {
  if (error) {
    return (
      <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load the mailbox status.</>}>
        <span className="[overflow-wrap:anywhere]">{error}</span>
      </Notice>
    );
  }
  if (status === null) return <Skeleton className="h-4 w-72" />;
  return (
    <div className="grid gap-2">
      <p className="m-0 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-fg-muted">
        <span>
          Mailbox{' '}
          {status.mailbox ? (
            <EmailLink address={status.mailbox} />
          ) : (
            <span className="text-fg-subtle">—</span>
          )}
        </span>
        <span>
          {status.enabled ? 'Last checked ' : 'Paused · last checked '}
          {status.lastPolledAt ? (
            <time dateTime={status.lastPolledAt} className="tabular">
              {formatDateTime(status.lastPolledAt)}
            </time>
          ) : (
            '—'
          )}
        </span>
      </p>
      {status.lastError && (
        <Notice tone="danger" role="alert" title={<>The last mailbox check failed.</>}>
          <span className="[overflow-wrap:anywhere]">
            {status.lastError}
            {status.lastErrorAt ? ` (${formatDateTime(status.lastErrorAt)})` : ''}
          </span>
        </Notice>
      )}
    </div>
  );
}

export default function EmailLogPage() {
  const { ability } = useAuth();
  const canRead = ability.canRead('email-log');
  const canTriage = ability.canTriage('email-log');
  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const query = useMemo(() => parseQuery(new URLSearchParams(queryString)), [queryString]);

  const [result, setResult] = useState<Paginated<EmailThread> | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const requestKey = `${queryString}|${reloadToken}|${showAll ? 'all' : 'paged'}`;
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const loading = loadedFor !== requestKey;
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const [mailbox, setMailbox] = useState<EmailLogStatus | null>(null);
  const [mailboxError, setMailboxError] = useState<string | null>(null);

  const [openThread, setOpenThread] = useState<EmailThread | null>(null);

  /* The search box is local state, debounced into the URL. */
  const [searchDraft, setSearchDraft] = useState(query.search);
  const [syncedSearch, setSyncedSearch] = useState(query.search);
  if (syncedSearch !== query.search) {
    setSyncedSearch(query.search);
    setSearchDraft(query.search);
  }

  const applyPatch = useCallback(
    (patch: Record<string, string>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === '') next.delete(key);
        else next.set(key, value);
      }
      if (!('page' in patch)) next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    const term = searchDraft.trim();
    if (term === query.search) return;
    const timer = window.setTimeout(() => applyPatch({ search: term }), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchDraft, query.search, applyPatch]);

  useEffect(() => {
    if (!canRead) return;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .getEmailLogStatus(signal)
      .then((res) => {
        if (signal.aborted) return;
        setMailbox(res.data);
        setMailboxError(null);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setMailboxError(
          caught instanceof ApiError && caught.status === 403
            ? DENIED
            : caught instanceof Error
              ? caught.message
              : 'Failed to load the mailbox status',
        );
      });
    return () => controller.abort();
  }, [canRead, reloadToken]);

  useEffect(() => {
    if (!canRead) return;
    const controller = new AbortController();
    const { signal } = controller;
    const current = parseQuery(new URLSearchParams(queryString));
    const params: EmailThreadListParams = showAll
      ? { status: current.status, page: 1, perPage: SHOW_ALL_PER_PAGE }
      : { status: current.status, page: current.page, perPage: PER_PAGE };
    if (current.search) params.search = current.search;
    const key = `${queryString}|${reloadToken}|${showAll ? 'all' : 'paged'}`;
    api
      .listEmailThreads(params, signal)
      .then((res) => {
        if (signal.aborted) return;
        setResult(res);
        setError(null);
        setForbidden(false);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(true);
          setResult(null);
          setError(null);
          return;
        }
        setError(caught instanceof Error ? caught.message : 'Failed to load the email log');
      })
      .finally(() => {
        if (!signal.aborted) setLoadedFor(key);
      });
    return () => controller.abort();
  }, [canRead, queryString, reloadToken, showAll]);

  if (!canRead) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader title="Email Log" />
        <Panel>
          <EmptyState icon={<InboxIcon />} title="Not available to your account" description={DENIED} />
        </Panel>
      </div>
    );
  }

  const rows = result?.data ?? [];
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const pageSize = showAll ? SHOW_ALL_PER_PAGE : PER_PAGE;
  const page = showAll ? 1 : query.page;
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader title="Email Log" />

      <MailboxStatus status={mailbox} error={mailboxError} />

      <Panel className="min-w-0">
        <div className="flex flex-wrap items-end gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 overflow-x-auto sm:w-auto">
            <SegmentedControl
              label="Status"
              value={query.status}
              options={TAB_OPTIONS}
              onChange={(next) => {
                setSearchDraft('');
                applyPatch({ status: next === 'unmatched' ? '' : next, search: '' });
              }}
              className="min-w-max"
            />
          </div>
          <div className="w-full min-w-0 sm:ml-auto sm:w-64">
            <Field label="Search">
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="search"
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                />
              )}
            </Field>
          </div>
        </div>

        {forbidden && (
          <Notice tone="danger" role="alert" className="m-4">
            {DENIED}
          </Notice>
        )}

        {error && (
          <Notice
            tone="danger"
            role="alert"
            className="m-4"
            title={<>Couldn&rsquo;t load the email log.</>}
            action={
              <Button variant="secondary" size="sm" onClick={() => setReloadToken((n) => n + 1)}>
                Retry
              </Button>
            }
          >
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        {!forbidden && (
          <div
            aria-busy={loading || undefined}
            className={cx(
              'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
              loading && result && 'opacity-60',
            )}
          >
            {!initialLoad && rows.length === 0 ? (
              error ? null : query.search ? (
                <EmptyState
                  icon={<InboxIcon />}
                  title="No threads match that search"
                  action={
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSearchDraft('');
                        applyPatch({ search: '' });
                      }}
                    >
                      Clear search
                    </Button>
                  }
                />
              ) : (
                <EmptyState icon={<InboxIcon />} title={EMPTY_TITLES[query.status]} />
              )
            ) : (
              <EmailThreadList rows={rows} loading={initialLoad} onOpen={setOpenThread} />
            )}
          </div>
        )}

        {!forbidden && result && total > 0 && (
          <ListPager
            page={page}
            totalPages={showAll ? 1 : totalPages}
            total={total}
            firstRow={firstRow}
            lastRow={lastRow}
            perPage={PER_PAGE}
            showAll={showAll}
            truncated={showAll && total > rows.length}
            loading={loading}
            noun={total === 1 ? 'thread' : 'threads'}
            onPage={(target) => applyPatch({ page: target <= 1 ? '' : String(target) })}
            onToggleShowAll={(next) => {
              setShowAll(next);
              if (next) applyPatch({ page: '' });
            }}
          />
        )}
      </Panel>

      <EmailThreadModal
        thread={openThread}
        canTriage={canTriage}
        onClose={() => setOpenThread(null)}
        onChanged={() => setReloadToken((n) => n + 1)}
      />
    </div>
  );
}

export { EmailLogPage };
